import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setUser } from '../../../store/actions';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { LoginDetailService } from '../../../services/ProfileService';
import Swal from 'sweetalert2';
import { EMAIL_REGEX } from '../../../utils/Constants';
import { StandardButton } from '../../common/GlobalElements/StandardButton';
import { LabelMedium } from '../../common/GlobalElements/Typography';
import { StandardErrorMessage } from '../../../utils/commonFunction';
import { tokens } from '../../../public/tokens';
import styled from 'styled-components';

const Required = styled.span({
    color: tokens.colors.states.alert[500],
    textAlign: 'center',
    fontFamily: tokens.globals['font-family'],
    fontSize: tokens.typography.fontSize['lg-14px'],
    fontWeight: tokens.typography.fontWeights.bold,
    lineHeight: '120%',
});

const LoginDetails = () => {
    const user = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    user.password = '';
    user.password_confirmation = '';

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: user,
        validationSchema: Yup.object({
            email: Yup.string()
                .matches(EMAIL_REGEX, 'Invalid email address')
                .email('Invalid email address')
                .required('Email is required'),
            password: Yup.string()
                .required('Password is required')
                .matches(
                    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
                    'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and one special case Character',
                ),
            password_confirmation: Yup.string()
                .required('Confirm Password is required')
                .oneOf([Yup.ref('password'), null], 'Passwords must match'),
        }),
        onSubmit: (values) => {
            setLoading(true);
            LoginDetailService(values)
                .then((res) => {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: 'Updated successfully',
                    }).then((result) => {
                        setLoading(false);
                        if (result.value) {
                            dispatch(setUser(res.data));
                        }
                    });
                })
                .catch((error) => {
                    setLoading(false);
                    Swal.close();
                    Swal.fire({
                        icon: 'error',
                        title: 'Email is not valid',
                        text: 'You cannot use an email that already exists',
                    }).then((result) => {});
                });
        },
    });

    /** Submit on Enter since there's no <form onSubmit> anymore. */
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            formik.handleSubmit();
        }
    };

    return (
        <>
            <div onKeyDown={handleKeyDown}>
                <div className="row">
                    <div className="col-md-12">
                        <div className="form-group">
                            <LabelMedium>
                                Email<Required>*</Required>
                            </LabelMedium>
                            <input
                                type="email"
                                className={
                                    'form-control' + (formik.errors.email && formik.touched.email ? ' is-invalid' : '')
                                }
                                name="email"
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                            <StandardErrorMessage formik={formik} name="email" />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group">
                            <LabelMedium>
                                Password<Required>*</Required>
                            </LabelMedium>
                            <input
                                type="password"
                                className={
                                    'form-control' +
                                    (formik.errors.password && formik.touched.password ? ' is-invalid' : '')
                                }
                                name="password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                            {formik.errors.password && formik.touched.password ? (
                                <span className="exclamation">
                                    <i className="fal fa-exclamation-circle"></i>
                                </span>
                            ) : null}
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group">
                            <LabelMedium>
                                Confirm Password<Required>*</Required>
                            </LabelMedium>
                            <input
                                type="password"
                                className={
                                    'form-control' +
                                    (formik.errors.password_confirmation && formik.touched.password_confirmation
                                        ? ' is-invalid'
                                        : '')
                                }
                                name="password_confirmation"
                                value={formik.values.password_confirmation}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                            {formik.errors.password_confirmation && formik.touched.password_confirmation ? (
                                <span className="exclamation">
                                    <i className="fal fa-exclamation-circle"></i>
                                </span>
                            ) : null}
                        </div>
                    </div>

                    <div className="col-md-3">
                        <StandardButton disabled={loading} type="button" onClick={formik.handleSubmit} title="Save">
                            {loading ? <i className="fas fa-cog fa-spin"></i> : <i className="fal fa-paper-plane"></i>}
                            Update Login Details
                        </StandardButton>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LoginDetails;
